import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { userManagerApi, venueManagerApi } from '../api/axios.config';

export interface CurrentUser {
  name: string;
  birthday?: Date;
  gender?: string;
  email: string;
  phone: string;
  productName: string;
  otp?: string;
}

interface CreateUserRequest {
  firstName: string;
  lastName: string;
  birthday?: Date;
  email: string;
  phone: string;
  productName: string;
  otp?: string;
}

interface PatchUserRequest {
  firstName: string;
  lastName: string;
  birthday?: Date;
  gender?: string;
}

export enum TrackEventsType {
  WAITLIST_ENTER = 'WAITLIST_ENTER',
  WAITLIST_LOGIN = 'WAITLIST_LOGIN'
}

interface CurrentUserContextProps {
  user: CurrentUser | null;
  getCurrentUser: () => Promise<void>;
  createUser: (payload: CreateUserRequest) => Promise<void>;
  loginWithPassport: (passport: string) => Promise<void>;
  patchUser: (payload: PatchUserRequest) => Promise<void>;
  sendOtp: (channel: string, target: string, venueId: string, lang?: string) => Promise<void>;
  logout: () => Promise<void>;
  registerEvent: (venueId: string, eventType: TrackEventsType, eventBody: any) => Promise<void>
}

const CurrentUserContext = createContext<CurrentUserContextProps | undefined>(undefined);

export const CurrentUserProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<CurrentUser | null>(null);

  const getCurrentUser = async () => {
    try {
      const res = await userManagerApi.get(`/product-user`, { withCredentials: true });
      hydrateUser(res.data);
    } catch (e) {
      setUser(null);
    }
  };

  const createUser = async (payload: CreateUserRequest) => {
    const res = await userManagerApi.post(`/product-user`, payload, { withCredentials: true });
    hydrateUser(res.data);
  };

  const loginWithPassport = async (passport: string) => {
    console.log("AAAAAAa")
    const res = await userManagerApi.post(`/product-user/oauth2/passport`, { passport, productName: "Tagme" }, { withCredentials: true });
    hydrateUser(res.data);
  };

  const patchUser = async (payload: PatchUserRequest) => {
    const res = await userManagerApi.patch(`/product-user`, payload, { withCredentials: true });
    hydrateUser(res.data);
  };

  const sendOtp = async (channel: string, target: string, venueId: string, lang?: string) => {
    const payload = { productName: 'Tagme', channel, target, venueId, type: 'validate', lang };
    await userManagerApi.post(`/otp/send`, payload, { withCredentials: true });
  };

  const logout = async () => {
    await userManagerApi.post(`/product-user/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  const registerEvent = async (venueId: string, eventType: TrackEventsType, eventBody: string) => {
    const payload = {
      venueId,
      eventType,
      eventBody
    }
    await userManagerApi.post(`/product-user/event`, payload, { withCredentials: true });
  };

  const hydrateUser = (rawUser: any) => {
    if (!rawUser) return setUser(null);
    setUser({
      name: rawUser.lastName ? `${rawUser.firstName} ${rawUser.lastName}` : rawUser.firstName,
      phone: rawUser.phone,
      email: rawUser.email,
      birthday: rawUser.birthday,
      gender: rawUser.gender,
      productName: 'Tagme'
    });
  };

  useEffect(() => {
    getCurrentUser();
  }, []);

  return (
    <CurrentUserContext.Provider
      value={{ user, getCurrentUser, createUser, loginWithPassport, patchUser, sendOtp, registerEvent, logout }}
    >
      {children}
    </CurrentUserContext.Provider>
  );
};

export const useCurrentUser = () => {
  const context = useContext(CurrentUserContext);
  if (!context) throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  return context;
};